/* eslint-disable camelcase */
import { SearchOutlined } from '@ant-design/icons';
import { Col, Row, Tooltip, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { ColumnFilterItem, ColumnsType, FilterConfirmProps } from 'antd/lib/table/interface';
import { BuildingFloor } from 'components/BuildingFloor/BuildingFloor';
import { Button } from 'components/Button/Button';
import { commodityTranslated } from 'components/Form/meter_details/constants';
import { Input } from 'components/Input/Input';
import dayjs from 'dayjs';
import React from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import { COMMODITY_CONFIG } from 'consts/commodities';
import { MeterListFilter } from '../const';
import { Columns as messages } from '../messages';
import { MetersTableMeter } from '../queries/getMetersTableData';

type FilterItems = keyof Pick<MeterListFilter, 'commodities' | 'locationBuildings' | 'buildingFloors'>;

interface GetColumnsConfig {
  activeFilter: MeterListFilter;
  filterItems: Record<FilterItems, ColumnFilterItem[]>;
  intl: IntlShape;
  onSearch: (
    selectedKeys: React.Key[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: keyof MeterListFilter,
  ) => void;
  onReset: (clearFilters: () => void, item: keyof MeterListFilter) => void;
}

export const getColumns = ({
  activeFilter,
  filterItems,
  intl,
  onSearch,
  onReset,
}: GetColumnsConfig): ColumnsType<MetersTableMeter> => {
  const { sortOrder, sortField } = activeFilter;

  const getColumnSearchProps = (
    dataIndex: keyof MeterListFilter,
  ): Pick<ColumnProps<MetersTableMeter>, 'filterDropdown' | 'filterIcon' | 'onFilter' | 'filteredValue'> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <form
        style={{ padding: 8 }}
        onSubmit={e => {
          e.preventDefault();
          onSearch(selectedKeys, confirm, dataIndex);
        }}
      >
        <Input
          placeholder={`Search ${dataIndex}`}
          style={{ marginBottom: 8, display: 'block' }}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys([e.target.value])}
        />
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Button block htmlType="submit" icon={<SearchOutlined />} size="small" type="primary">
              Search
            </Button>
          </Col>
          <Col span={12}>
            <Button
              block
              size="small"
              style={{ whiteSpace: 'pre-wrap' }}
              onClick={() => clearFilters && onReset(clearFilters, dataIndex)}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </form>
    ),
    filterIcon: () => <SearchOutlined />,
    filteredValue: activeFilter[dataIndex] != null ? [String(activeFilter[dataIndex])] : null,
  });

  return [
    {
      width: 80,
      title: 'Id',
      dataIndex: ['toolsId' satisfies keyof MetersTableMeter],
      ellipsis: true,
      fixed: 'left',
      key: 'toolsId' satisfies keyof MetersTableMeter,
      sorter: true,
      sortOrder: sortField === ('toolsId' satisfies keyof MetersTableMeter) ? sortOrder : undefined,
      render: (_, { toolsId }) => <Link to={`/meter/${toolsId}`}>{toolsId}</Link>,
    },
    {
      width: 150,
      title: <FormattedMessage {...messages.monitoredEntity} />,
      dataIndex: ['name' satisfies keyof MetersTableMeter],
      ellipsis: true,
      key: 'name' satisfies keyof MetersTableMeter,
      onCell: () => ({
        style: {
          whiteSpace: 'nowrap',
          maxWidth: 150,
        },
      }),
      sorter: true,
      sortOrder: sortField === ('name' satisfies keyof MetersTableMeter) ? sortOrder : undefined,
      render: (_, { name }) => (
        <Tooltip title={name}>
          <Typography.Text>{name}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      width: 60,
      title: <FormattedMessage {...messages.commodity} />,
      dataIndex: ['commodity' satisfies keyof MetersTableMeter],
      key: 'commodities' satisfies keyof MeterListFilter,
      sorter: (a: MetersTableMeter, b: MetersTableMeter) => {
        return COMMODITY_CONFIG[a.commodity].priority - COMMODITY_CONFIG[b.commodity].priority;
      },
      filters: filterItems.commodities,
      filteredValue: activeFilter.commodities,
      sortOrder: sortField === ('commodity' satisfies keyof MetersTableMeter) ? sortOrder : undefined,
      render: (_, { commodity }) => (commodity ? intl.formatMessage(commodityTranslated[commodity]) : ''),
    },
    {
      width: 60,
      title: <FormattedMessage {...messages.serialNumber} />,
      dataIndex: ['serial' satisfies keyof MetersTableMeter],
      key: 'serialNumbers' satisfies keyof MeterListFilter,
      sorter: true,
      sortOrder: sortField === ('serial' satisfies keyof MetersTableMeter) ? sortOrder : undefined,
      render: (_, { serial }) => serial || '',
      ...getColumnSearchProps('serialNumbers' satisfies keyof MeterListFilter),
    },
    {
      width: 60,
      title: <FormattedMessage {...messages.building} />,
      dataIndex: ['inventory_location_building', 'name'],
      key: 'locationBuildings' satisfies keyof MeterListFilter,
      filters: filterItems.locationBuildings,
      filteredValue: activeFilter.locationBuildings,
    },
    {
      width: 20,
      title: <FormattedMessage {...messages.floor} />,
      dataIndex: ['buildingFloor' satisfies keyof MetersTableMeter],
      key: 'buildingFloors' satisfies keyof MeterListFilter,
      sorter: true,
      filters: filterItems.buildingFloors,
      filteredValue: activeFilter.buildingFloors,
      sortOrder: sortField === 'buildingFloor' ? sortOrder : undefined,
      render: (_, { buildingFloor }) => <BuildingFloor level={buildingFloor} />,
    },
    {
      width: 60,
      title: <FormattedMessage {...messages.lastUpdate} />,
      dataIndex: ['updatedAt' satisfies keyof MetersTableMeter],
      key: 'updatedAt' satisfies keyof MetersTableMeter,
      sorter: true,
      sortOrder: sortField === ('updatedAt' satisfies keyof MetersTableMeter) ? sortOrder : undefined,
      render: (_, { updatedAt }) => (updatedAt ? dayjs(updatedAt).format('DD.MM.YYYY HH:mm') : ''),
    },
    {
      width: 20,
      title: <FormattedMessage {...messages.action} />,
      key: 'actions',
      render: (_, record) => <Link to={`/meter/${record.toolsId}`}>Detail</Link>,
    },
  ];
};
